import React, { useEffect, useState } from 'react';
import { FaDownload, FaEnvelope, FaPalette, FaRegFileAlt, FaExternalLinkAlt, FaGithub, FaGraduationCap } from 'react-icons/fa';
import { generatePDF, generateTablePDF } from '../utils/pdfGenerator';
import { getPortfolioData, getAboutData } from '../services/supabaseService';
import { THEME_PRESETS, applyThemeSettings, getSiteSettings } from '../utils/siteSettings';
import { safeParseJSON } from '../utils/storage';
import './Home.css';

const AYAH_ROTATION_MS = 5000;
const AYAH_REFERENCES = [
  '97:1','94:6','93:5','65:3','2:286','2:255','3:8','3:26','3:173',
  '4:59','4:135','4:147','5:8','5:35','5:54','6:17','6:59','6:103',
  '7:23','7:56','7:180','7:199','7:205','8:2','9:40','9:51','9:111',
  '10:62','10:107','11:90','11:114','12:87','13:11','13:28','14:7',
  '14:34','15:49','16:53','16:97','17:23','17:36','17:80','17:84',
  '18:10','18:46','18:65','20:46','21:35','22:5','22:46','23:96',
  '24:35','24:37','25:63','25:70','28:77','29:2','29:69','30:21',
  '31:33','32:7','33:17','33:41','34:39','35:3','36:82','37:180',
  '38:35','39:10','39:53','40:60','41:30','41:34','42:40','43:14',
  '46:13','47:7','49:10','49:13','50:16','51:17','51:58','55:13',
  '55:60','57:1','57:4','58:11','59:18','60:7','61:13','64:14',
  '65:2','66:6','67:2','68:4','70:19','73:8','76:8','84:6','86:17',
  '93:7','94:5','95:6','103:3','105:1','107:7','109:6','112:1','113:1'
];
const AYAH_EDITIONS = 'quran-uthmani,ur.jalandhry';

const MAX_URDU_LINE_CHARS = 48;

const FALLBACK_AYAHS = [
  { arabic: 'إِنَّ مَعَ الْعُسْرِ يُسْرًا', urdu: 'بے شک تنگی کے ساتھ آسانی ہے۔', reference: 'الشرح ۵' },
  { arabic: 'وَرَبَّكَ فَكَبِّرْ', urdu: 'اور اپنے رب کی بڑائی بیان کرو۔', reference: 'المدثر ۳' },
  { arabic: 'قُلْ هُوَ اللَّهُ أَحَدٌ', urdu: 'کہہ دیجیے کہ وہ اللہ ایک ہے۔', reference: 'الاخلاص ۱' },
  { arabic: 'وَقُولُوا لِلنَّاسِ حُسْنًا', urdu: 'اور لوگوں سے اچھی بات کہو۔', reference: 'البقرہ ۸۳' },
  { arabic: 'وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ', urdu: 'اور نماز قائم کرو اور زکوٰۃ دو۔', reference: 'البقرہ ۱۱۰' },
  { arabic: 'إِنَّ اللَّهَ عَلِيمٌ خَبِيرٌ', urdu: 'بے شک اللہ خوب جاننے والا اور خبردار ہے۔', reference: 'الانفال ۴۲' },
  { arabic: 'وَاللَّهُ يَعْلَمُ وَأَنْتُمْ لَا تَعْلَمُونَ', urdu: 'اور اللہ جانتا ہے اور تم نہیں جانتے۔', reference: 'البقرہ ۲۱۶' },
  { arabic: 'وَمَنْ يَعْمَلْ مِثْقَالَ ذَرَّةٍ خَيْرًا يَرَهُ', urdu: 'اور جس نے ذرّہ برابر بھی نیکی کی وہ اسے دیکھ لے گا۔', reference: 'الزلزال ۷' },
  { arabic: 'لَا يُكَلِّفُ اللَّهُ نَفْسًا إِلَّا وُسْعَهَا', urdu: 'اللہ کسی جان پر اس کی وسعت سے زیادہ ذمہ داری نہیں دیتا۔', reference: 'البقرہ ۲۸۶' },
  { arabic: 'إِنَّ اللَّهَ مَعَ الصَّابِرِينَ', urdu: 'بے شک اللہ صبر کرنے والوں کے ساتھ ہے۔', reference: 'البقرہ ۱۵۳' },
  { arabic: 'وَمَا تَوْفِيقِي إِلَّا بِاللَّهِ', urdu: 'اور میری توفیق صرف اللہ کی ذات سے ہے۔', reference: 'هود ۸۸' },
  { arabic: 'وَلَقَدْ كَرَّمْنَا بَنِي آدَمَ', urdu: 'اور بے شک ہم نے بنی آدم کو باعزت بنایا۔', reference: 'الاسراء ۷۰' },
  { arabic: 'إِنَّ الصَّلَاةَ تَنْهَىٰ عَنِ الْفَحْشَاءِ وَالْمُنْكَرِ', urdu: 'بے شک نماز بے حیائی اور برائی سے روکتی ہے۔', reference: 'العنکبوت ۴۵' },
  { arabic: 'يُضِلُّ بِهِ كَثِيرًا وَيَهْدِي بِهِ كَثِيرًا', urdu: 'اس سے بہتوں کو گمراہ کرتا ہے اور بہتوں کو ہدایت دیتا ہے۔', reference: 'البقرہ ۲۶' },
  { arabic: 'فَإِنَّ مَعَ الْعُسْرِ يُسْرًا إِنَّ مَعَ الْعُسْرِ يُسْرًا', urdu: 'پس بے شک تنگی کے ساتھ آسانی ہے۔', reference: 'الشرح ۵-۶' },
  { arabic: 'وَإِنْ تَعُدُّوا نِعْمَتَ اللَّهِ لَا تُحْصُوهَا', urdu: 'اور اگر تم اللہ کی نعمتوں کا شمار کرو تو اسے گن نہیں سکتے۔', reference: 'النحل ۱۸' },
  { arabic: 'أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ', urdu: 'اللہ کے ذکر سے دلوں کو اطمینان حاصل ہوتا ہے۔', reference: 'الرعد ۲۸' },
  { arabic: 'وَمَنْ يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ', urdu: 'اور جو اللہ پر توکل کرے تو وہ اس کے لیے کافی ہے۔', reference: 'الطلاق ۳' },
  { arabic: 'وَمَنْ يَتَّقِ اللَّهَ يَجْعَلْ لَهُ مَخْرَجًا', urdu: 'اور جو اللہ کا تقویٰ اختیار کرے اللہ اس کے لیے راستہ بناتا ہے۔', reference: 'الطلاق ۲' },
  { arabic: 'وَيَرْزُقْهُ مِنْ حَيْثُ لَا يَحْتَسِبُ', urdu: 'اور اسے وہاں سے رزق دیتا ہے جہاں سے اسے گمان بھی نہیں ہوتا۔', reference: 'الطلاق ۳' },
  { arabic: 'إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ', urdu: 'بے شک ہم اللہ ہی کے ہیں اور اللہ ہی کی طرف لوٹنے والے ہیں۔', reference: 'البقرہ ۱۵۶' },
  { arabic: 'رَبِّ زِدْنِي عِلْمًا', urdu: 'اے میرے رب! میرے علم میں اضافہ فرما۔', reference: 'طہ ۱۱۴' },
  { arabic: 'وَجَعَلْنَا لَكُمْ فِيهَا مَعَايِشَ', urdu: 'اور ہم نے ان میں تمہارے لیے روزی کے سامان بنائے۔', reference: 'الحجر ۲۰' },
  { arabic: 'وَاللَّهُ خَيْرُ الرَّازِقِينَ', urdu: 'اور اللہ سب سے بہتر رزق دینے والا ہے۔', reference: 'الجمعة ۱۱' },
  { arabic: 'وَأَحْسِنُوا ۛ إِنَّ اللَّهَ يُحِبُّ الْمُحْسِنِينَ', urdu: 'اور نیکی کرو، بے شک اللہ نیکی کرنے والوں سے محبت فرماتا ہے۔', reference: 'البقرہ ۱۹۵' },
  { arabic: 'قُلْ أَعُوذُ بِرَبِّ الْفَلَقِ', urdu: 'کہہ دیجیے کہ میں صبح کے رب کی پناہ مانگتا ہوں۔', reference: 'الفلق ۱' },
  { arabic: 'قُلْ أَعُوذُ بِرَبِّ النَّاسِ', urdu: 'کہہ دیجیے کہ میں لوگوں کے رب کی پناہ مانگتا ہوں۔', reference: 'الناس ۱' },
  { arabic: 'رَبِّ اشْرَحْ لِي صَدْرِي', urdu: 'اے میرے رب! میرے لیے میرا سینہ کشادہ فرما۔', reference: 'طہ ۲۵' },
  { arabic: 'وَيَسِّرْ لِي أَمْرِي', urdu: 'اور میرے لیے میرا کام آسان بنا دے۔', reference: 'طہ ۲۶' },
  { arabic: 'رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً', urdu: 'اے ہمارے رب! ہمیں دنیا میں بھی نیکی دے اور آخرت میں بھی نیکی دے۔', reference: 'البقرہ ۲۰۱' }
];

const getAyahApiUrl = (reference) =>
  `https://api.alquran.cloud/v1/ayah/${reference}/editions/${AYAH_EDITIONS}`;

const trimArabicLetters = (text = '', maxLetters = 200) => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  let result = '';
  let letters = 0;

  for (const char of normalized) {
    if (/\s/u.test(char)) {
      result += char;
      continue;
    }
    if (letters >= maxLetters) {
      break;
    }
    result += char;
    letters += 1;
  }

  return result.trim();
};

const Home = ({ userData }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [localUserData, setLocalUserData] = useState(userData);
  const [temporaryThemeKey, setTemporaryThemeKey] = useState('');
  const [baseThemeSettings] = useState(() => getSiteSettings());
  const [ayahItems, setAyahItems] = useState(FALLBACK_AYAHS);
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0);
  const [isAyahLoading, setIsAyahLoading] = useState(true);
  const [homePopup, setHomePopup] = useState({
    enabled: false,
    title: 'Announcement',
    message: '',
    imageUrl: '',
    buttonText: 'Close'
  });
  const [showHomePopup, setShowHomePopup] = useState(false);

  const dailyAyah = ayahItems[currentAyahIndex] || FALLBACK_AYAHS[0];

  const handleDownloadCV = async () => {
    setIsGenerating(true);
    try {
      const [portfolioResult, aboutResult] = await Promise.all([getPortfolioData(), getAboutData()]);
      const portfolioData = portfolioResult.success ? portfolioResult.data : null;
      const aboutData = aboutResult.success ? aboutResult.data : null;
      await generatePDF(userData, portfolioData, aboutData);
    } catch (error) {
      console.error('Error fetching data for PDF:', error);
      alert('Error loading data. Generating PDF with available information...');
      await generatePDF(userData, null, null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadTableCV = async () => {
    setIsGenerating(true);
    try {
      const [portfolioResult, aboutResult] = await Promise.all([getPortfolioData(), getAboutData()]);
      const portfolioData = portfolioResult.success ? portfolioResult.data : null;
      const aboutData = aboutResult.success ? aboutResult.data : null;
      await generateTablePDF(userData, portfolioData, aboutData);
    } catch (error) {
      console.error('Error fetching data for table PDF:', error);
      alert('Error loading data. Generating table PDF with available information...');
      await generateTablePDF(userData, null, null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleContactMe = () => {
    window.open('https://wa.me/923046983794', '_blank');
  };

  const handleTemporaryThemeApply = (presetKey) => {
    const preset = THEME_PRESETS[presetKey];
    if (!preset) {
      return;
    }

    const temporarySettings = {
      ...baseThemeSettings,
      theme: {
        ...baseThemeSettings.theme,
        ...preset.theme
      },
      sidebar: {
        ...baseThemeSettings.sidebar,
        ...preset.sidebar
      }
    };

    applyThemeSettings(temporarySettings);
    setTemporaryThemeKey(presetKey);
  };

  const handleTemporaryThemeReset = () => {
    applyThemeSettings(baseThemeSettings);
    setTemporaryThemeKey('');
  };

  const handleTemporaryThemeSelectChange = (event) => {
    const selectedPresetKey = event.target.value;
    if (!selectedPresetKey) {
      handleTemporaryThemeReset();
      return;
    }
    handleTemporaryThemeApply(selectedPresetKey);
  };

  useEffect(() => {
    let mounted = true;

    const loadAyahs = async () => {
      setIsAyahLoading(true);
      try {
        const responses = await Promise.allSettled(
          AYAH_REFERENCES.map(async (reference) => {
            const response = await fetch(getAyahApiUrl(reference), {
              headers: {
                Accept: 'application/json'
              }
            });

            if (!response.ok) {
              throw new Error(`Quran API failed for ${reference}`);
            }

            const payload = await response.json();
            const editions = Array.isArray(payload?.data) ? payload.data : [];
            const arabicEdition = editions.find((item) => item?.edition?.language === 'ar');
            const urduEdition = editions.find((item) => item?.edition?.language === 'ur');

            const arabic = trimArabicLetters(arabicEdition?.text?.trim() || '');
            const urdu = urduEdition?.text?.trim() || '';

            if (!arabic || !urdu) {
              throw new Error(`Invalid ayah payload for ${reference}`);
            }

            if (urdu.length > MAX_URDU_LINE_CHARS) {
              throw new Error(`Ayah too long for one line: ${reference}`);
            }

            return { arabic, urdu, reference };
          })
        );

        const loadedAyahs = responses
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value)
          .filter((ayah) => ayah.urdu.length <= MAX_URDU_LINE_CHARS);

        if (mounted) {
          setAyahItems(loadedAyahs.length > 0 ? loadedAyahs : FALLBACK_AYAHS);
          setCurrentAyahIndex(0);
        }
      } catch (error) {
        console.error('Error loading Quran ayahs:', error);
        if (mounted) {
          setAyahItems(FALLBACK_AYAHS);
          setCurrentAyahIndex(0);
        }
      } finally {
        if (mounted) {
          setIsAyahLoading(false);
        }
      }
    };

    loadAyahs();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (ayahItems.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCurrentAyahIndex((prev) => (prev + 1) % ayahItems.length);
    }, AYAH_ROTATION_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [ayahItems]);

  useEffect(() => {
    const loadLocalData = () => {
      const savedSocialLinks = localStorage.getItem('socialLinks');
      const savedHelloText = localStorage.getItem('helloText');
      const savedPopupSettings = localStorage.getItem('homePopupSettings');

      if (savedSocialLinks || savedHelloText) {
        setLocalUserData((prev) => ({
          ...prev,
          socialLinks: safeParseJSON(savedSocialLinks, prev.socialLinks, 'social links') || prev.socialLinks,
          helloText: savedHelloText || prev.helloText
        }));
      }

      if (savedPopupSettings) {
        const popupSettings = safeParseJSON(savedPopupSettings, {}, 'home popup settings');
        const nextPopup = {
          enabled: Boolean(popupSettings.enabled),
          title: popupSettings.title || 'Announcement',
          message: Array.isArray(popupSettings.sentences)
            ? popupSettings.sentences.filter(Boolean).join('\n')
            : popupSettings.message || '',
          imageUrl: popupSettings.imageUrl || '',
          buttonText: popupSettings.buttonText || 'Close'
        };

        setHomePopup(nextPopup);
        setShowHomePopup(Boolean(nextPopup.enabled && (nextPopup.message || nextPopup.imageUrl)));
      }
    };

    loadLocalData();
    window.addEventListener('storage', loadLocalData);
    return () => window.removeEventListener('storage', loadLocalData);
  }, []);

  useEffect(() => {
    setLocalUserData(userData);
  }, [userData]);

  useEffect(() => {
    const fullText = localUserData.summary?.replace(/ - /g, ' - ') || '';
    if (!fullText) {
      return undefined;
    }

    let currentIndex = 0;
    let typingInterval = null;
    let pauseTimeout = null;

    const startTyping = () => {
      setDisplayedText('');
      setIsTyping(true);
      currentIndex = 0;

      typingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          setDisplayedText(fullText.substring(0, currentIndex + 1));
          currentIndex += 1;
          return;
        }

        setIsTyping(false);
        clearInterval(typingInterval);
        pauseTimeout = setTimeout(() => startTyping(), 2000);
      }, 50);
    };

    startTyping();

    return () => {
      if (typingInterval) {
        clearInterval(typingInterval);
      }
      if (pauseTimeout) {
        clearTimeout(pauseTimeout);
      }
    };
  }, [localUserData.summary]);

  return (
    <div className="home">
      {showHomePopup && homePopup.enabled && (homePopup.message || homePopup.imageUrl) && (
        <div className="home-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="home-popup-title">
          <div className="home-popup-card">
            <button
              type="button"
              className="home-popup-close"
              onClick={() => setShowHomePopup(false)}
              aria-label="Close popup"
            >
              ×
            </button>
            <div className="home-popup-label">Notice</div>
            <h2 id="home-popup-title">{homePopup.title}</h2>
            {homePopup.imageUrl && (
              <div className="home-popup-image">
                <img
                  src={homePopup.imageUrl}
                  alt={homePopup.title || 'Popup'}
                  onError={(e) => {
                    e.currentTarget.parentElement.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  className="home-popup-download-btn"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const response = await fetch(homePopup.imageUrl);
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'business-card.png';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Error downloading image:', error);
                      const a = document.createElement('a');
                      a.href = homePopup.imageUrl;
                      a.download = 'business-card.png';
                      a.target = '_blank';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }
                  }}
                  title="Download Image"
                  aria-label="Download Image"
                >
                  <FaDownload />
                </button>
              </div>
            )}
            <div className="home-popup-message">
              {homePopup.message.split('\n').filter(Boolean).map((sentence, index) => (
                <p key={index}>{sentence}</p>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-primary home-popup-action"
              onClick={() => setShowHomePopup(false)}
            >
              {homePopup.buttonText || 'Close'}
            </button>
          </div>
        </div>
      )}

      <div className="home-container">
        <div className="hero-section">
          <div className="theme-preview-switcher">
            <div className="theme-preview-row">
              <div className="theme-preview-control">
                <div className="theme-preview-header">
                  <FaPalette />
                  <span>Try Theme (Temporary)</span>
                </div>
                <select
                  className="theme-preview-select"
                  value={temporaryThemeKey}
                  onChange={handleTemporaryThemeSelectChange}
                  aria-label="Theme preview selector"
                >
                  <option value="">Default (Saved)</option>
                  {Object.entries(THEME_PRESETS).map(([presetKey, preset]) => (
                    <option key={presetKey} value={presetKey}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ayah-preview-card">
                <div className="ayah-card-badges">
                  <span className="ayah-badge-primary">قرآنی آیت</span>
                  {!isAyahLoading && dailyAyah.reference && (
                    <span className="ayah-badge-secondary">{dailyAyah.reference}</span>
                  )}
                </div>
                <p className="ayah-one-line" dir="rtl" lang="ur">
                  {isAyahLoading ? '...' : dailyAyah.urdu}
                </p>
              </div>
            </div>
          </div>

          <div className="hello-badge">{localUserData.helloText || 'AsslamuAlikum'}</div>
          <div className="hero-content">
            <div className="hero-image">
              {localUserData.profileImage ? (
                <img src={localUserData.profileImage} alt="Profile" />
              ) : (
                <div className="default-hero-avatar">
                  {localUserData.firstName.charAt(0)}
                  {localUserData.lastName.charAt(0)}
                </div>
              )}
            </div>
            <div className="hero-text">
              <h1 className="hero-title">
                {localUserData.firstName} {localUserData.lastName}
              </h1>
              <p className="hero-summary">
                {displayedText}
                {isTyping && <span className="typing-cursor">|</span>}
              </p>
            </div>
          </div>
        </div>

        <div className="personal-info">
          <div className="info-tables-container">
            <table className="info-table">
              <tbody>
                <tr className="info-row">
                  <td className="info-cell">
                    <div className="info-icon">👤</div>
                    <span className="info-label">Full Name:</span>
                  </td>
                  <td className="info-cell">
                    <span className="info-value">
                      {localUserData.firstName} {localUserData.lastName}
                    </span>
                  </td>
                </tr>
                <tr className="info-row">
                  <td className="info-cell">
                    <div className="info-icon">📞</div>
                    <span className="info-label">Phone:</span>
                  </td>
                  <td className="info-cell">
                    <span className="info-value">{localUserData.phone}</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="info-table">
              <tbody>
                <tr className="info-row">
                  <td className="info-cell">
                    <div className="info-icon">✉️</div>
                    <span className="info-label">Email Address:</span>
                  </td>
                  <td className="info-cell">
                    <span className="info-value">{localUserData.email}</span>
                  </td>
                </tr>
                <tr className="info-row">
                  <td className="info-cell">
                    <div className="info-icon">💼</div>
                    <span className="info-label">Professional Title:</span>
                  </td>
                  <td className="info-cell">
                    <span className="info-value">{localUserData.title}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Featured Project Portfolios Showcase */}
        <div className="featured-projects-section">
          <div className="featured-projects-grid">
            <a
              href="https://abdullahstduenstprojects.lovable.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="featured-project-card featured-project-card-students"
            >
              <div className="project-card-top">
                <span className="project-badge project-badge-students">
                  <FaGraduationCap className="badge-icon" /> Mentored Students Archive
                </span>
                <span className="project-external-hint">
                  <FaExternalLinkAlt />
                </span>
              </div>
              <h3 className="project-card-title">
                Students' GitHub Projects
              </h3>
              <p className="project-card-desc">
                Comprehensive archive of real-world web apps, final year capstones &amp; GitHub repositories developed by students under my mentorship to date.
              </p>
              <div className="project-card-tags">
                <span className="project-tag">Students' Work</span>
                <span className="project-tag">GitHub Repos</span>
                <span className="project-tag">Live Demos</span>
              </div>
              <div className="project-card-btn">
                <span>Explore Students' Projects</span>
                <FaExternalLinkAlt className="btn-inline-icon" />
              </div>
            </a>

            <a
              href="https://abdullahportfolioprojects.lovable.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="featured-project-card featured-project-card-personal"
            >
              <div className="project-card-top">
                <span className="project-badge project-badge-personal">
                  <FaGithub className="badge-icon" /> Developer Portfolio Hub
                </span>
                <span className="project-external-hint">
                  <FaExternalLinkAlt />
                </span>
              </div>
              <h3 className="project-card-title">
                My GitHub Projects Portfolio
              </h3>
              <p className="project-card-desc">
                Curated portfolio featuring my personal full-stack software, AI automation tools, custom systems &amp; open-source GitHub repositories.
              </p>
              <div className="project-card-tags">
                <span className="project-tag">Personal Portfolio</span>
                <span className="project-tag">AI &amp; Full-Stack</span>
                <span className="project-tag">Source Code</span>
              </div>
              <div className="project-card-btn">
                <span>Explore My GitHub Projects</span>
                <FaExternalLinkAlt className="btn-inline-icon" />
              </div>
            </a>
          </div>
        </div>

        {localUserData.socialLinks && localUserData.socialLinks.length > 0 && (
          <div className="social-links-section">
            <h3>Follow Me</h3>
            <div className="social-grid">
              {localUserData.socialLinks.map(
                (link) =>
                  link.url && (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <i className={link.icon} style={{ color: link.color }} />
                      {link.platform}
                    </a>
                  )
              )}
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button className="btn btn-secondary" onClick={handleDownloadCV} disabled={isGenerating}>
            <FaDownload className="btn-icon" />
            {isGenerating ? 'Generating PDF...' : 'Download Resume'}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadTableCV} disabled={isGenerating}>
            <FaDownload className="btn-icon" />
            {isGenerating ? 'Generating PDF...' : 'Table Resume'}
          </button>
          <a
            className="btn btn-secondary btn-generic-resume"
            href="https://docs.google.com/document/d/1PnwmJI-nFWYevyHDlFH68VFxybnxeBgwr1530dk9B50/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaRegFileAlt className="btn-icon" />
            Generic Resume
          </a>
          <button className="btn btn-primary" onClick={handleContactMe}>
            <FaEnvelope className="btn-icon" />
            Contact Me
          </button>
        </div>
      </div>

      <footer className="footer">
        <p>
          © 2026 {localUserData.firstName} {localUserData.lastName}. All Rights Reserved.
        </p>
      </footer>
    </div>
  );
};

export default Home;
